import type {
  ComponentPropsWithoutRef,
  ElementRef,
  HTMLAttributes,
  PropsWithChildren,
} from "react";
import { createContext, forwardRef, useContext } from "react";
import { tv } from "tailwind-variants";
import type { VariantProps } from "tailwind-variants";

import { VARIANT } from "@/shared/models/legacy-constants";
import { Heading } from "@/shared/ui/common/heading";
import { AltArrowLeftIcon } from "../icons";
import type { BreadcrumbProps } from "./breadcrumb";
import { Breadcrumb as BaseBreadcrumb } from "./breadcrumb";
import { Button as BaseButton } from "./button";
import { IconWrapper } from "./icons";

const headerVariants = tv({
  slots: {
    root: "relative z-10 w-full shrink-0 overflow-hidden",
    content: "flex flex-col gap-0.5 md:gap-3",
    breadcrumb: "hidden md:block",
    description: "text-sm font-medium",
    actions: "flex items-center gap-3 self-start",
    button:
      "disabled:text-opacity-60 text-sm has-[svg]:p-4 has-[svg]:py-3.5 md:px-6 md:py-3 md:text-base [&>*]:not-[svg]:hidden md:[&>*]:not-[svg]:inline",
  },

  variants: {
    variant: {
      primary: {
        root: "rounded-2xl bg-primary-50",
        content: "p-5",
        description: "text-primary-950",
      },
      secondary: {
        content: "p-0 md:py-5",
        description: "text-primary-950",
      },
    },
  },
});

const { root, content, breadcrumb, description, actions, button } =
  headerVariants();

type HeaderVariant = typeof VARIANT.PRIMARY | typeof VARIANT.SECONDARY;

const HeaderVariantContext = createContext<HeaderVariant>(VARIANT.PRIMARY);

const Root = forwardRef<
  HTMLElement,
  HTMLAttributes<HTMLElement> & VariantProps<typeof headerVariants>
>(({ children, className, variant = VARIANT.PRIMARY, ...props }, ref) => {
  return (
    <HeaderVariantContext.Provider value={variant}>
      <header ref={ref} className={root({ variant, className })} {...props}>
        {children}
      </header>
    </HeaderVariantContext.Provider>
  );
});
Root.displayName = "Header.Root";

const Content = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const variant = useContext(HeaderVariantContext);
    return (
      <div ref={ref} className={content({ variant, className })} {...props} />
    );
  },
);
Content.displayName = "Header.Content";

const Breadcrumb = ({ className, ...props }: BreadcrumbProps) => {
  return <BaseBreadcrumb className={breadcrumb({ className })} {...props} />;
};
Breadcrumb.displayName = "Header.Breadcrumb";

const Title = ({ ...props }: PropsWithChildren) => {
  return (
    <Heading data-slot="header-title" level={1} color="primary" {...props} />
  );
};
Title.displayName = "Header.Title";

const Description = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const variant = useContext(HeaderVariantContext);
  return (
    <p ref={ref} className={description({ variant, className })} {...props} />
  );
});
Description.displayName = "Header.Description";

const Actions = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={actions({ className })} {...props} />
  ),
);
Actions.displayName = "Header.Actions";

const GoBackButton = (props: HTMLAttributes<HTMLButtonElement>) => {
  return (
    <IconWrapper size="sm" as="button" type="button" {...props}>
      <AltArrowLeftIcon />
    </IconWrapper>
  );
};
GoBackButton.displayName = "Header.GoBackButton";

const Button = forwardRef<
  ElementRef<typeof BaseButton>,
  ComponentPropsWithoutRef<typeof BaseButton>
>(({ className, ...props }, ref) => {
  const variant = useContext(HeaderVariantContext);

  return (
    <BaseButton
      ref={ref}
      size="lg"
      className={button({ variant, className })}
      {...props}
    />
  );
});
Button.displayName = "Header.Button";

export const Header = {
  Root,
  Content,
  Breadcrumb,
  Title,
  Description,
  Actions,
  Button,
  GoBackButton,
};
